import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Video, FileText, ClipboardList, CalendarClock, Bell, Loader2 } from 'lucide-react';

// One icon per notification type produced by backend/get-notifications.php
const icons = {
  recording: Video,
  class: CalendarClock,
  note: FileText,
  assignment: ClipboardList,
};

/**
 * "2 hours ago" from an ISO timestamp. The server sends IST wall-clock strings
 * (db.php sets +05:30), so tag them before parsing or every relative time is
 * off by the viewer's offset.
 */
export function timeAgo(iso) {
  if (!iso) return '';
  const d = new Date(/[+-]\d{2}:\d{2}$/.test(iso) ? iso : iso + '+05:30');
  if (isNaN(d)) return '';
  const secs = Math.floor((Date.now() - d.getTime()) / 1000);
  if (secs < 60) return 'Just now';
  const mins = Math.floor(secs / 60);
  if (mins < 60) return `${mins} minute${mins === 1 ? '' : 's'} ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs} hour${hrs === 1 ? '' : 's'} ago`;
  const days = Math.floor(hrs / 24);
  if (days < 7) return `${days} day${days === 1 ? '' : 's'} ago`;
  return d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
}

export default function NotificationDropdown({ notifications, loading, onClose }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 10, scale: 0.95 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={{ opacity: 0, y: 10, scale: 0.95 }}
      className="absolute right-0 top-full mt-2 w-80 bg-card rounded-xl shadow-2xl border overflow-hidden z-50"
    >
      <div className="p-4 border-b">
        <h3 className="font-heading font-semibold">Notifications</h3>
      </div>

      <div className="max-h-80 overflow-y-auto">
        {loading ? (
          <div className="p-8 flex items-center justify-center text-muted-foreground text-sm">
            <Loader2 className="w-4 h-4 animate-spin mr-2" /> Loading…
          </div>
        ) : notifications.length === 0 ? (
          <div className="p-8 text-center">
            <div className="w-10 h-10 rounded-full bg-muted flex items-center justify-center mx-auto mb-3">
              <Bell className="w-4 h-4 text-muted-foreground" />
            </div>
            <p className="text-sm font-medium">You're all caught up</p>
            <p className="text-xs text-muted-foreground mt-1">
              New recordings, classes and materials for your course will show up here.
            </p>
          </div>
        ) : (
          notifications.map((notif) => {
            const Icon = icons[notif.type] || Bell;
            return (
              <Link
                key={notif.id}
                to={notif.link || '/dashboard'}
                onClick={onClose}
                className={`block p-4 border-b last:border-0 hover:bg-muted/50 transition-colors ${
                  !notif.read ? 'bg-primary/5' : ''
                }`}
              >
                <div className="flex gap-3">
                  <div
                    className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${
                      !notif.read ? 'bg-primary/10' : 'bg-muted'
                    }`}
                  >
                    <Icon className={`w-4 h-4 ${!notif.read ? 'text-primary' : 'text-muted-foreground'}`} />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium truncate">{notif.title}</p>
                    <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">{notif.message}</p>
                    <p className="text-xs text-muted-foreground/60 mt-1">
                      {notif.course ? `${notif.course} · ` : ''}{timeAgo(notif.event_at)}
                    </p>
                  </div>
                </div>
              </Link>
            );
          })
        )}
      </div>
    </motion.div>
  );
}
